/**
 * Business Intelligence API service
 * 
 * Handles all API calls for business intelligence data management
 */

import { get, post, put, ApiError } from './api';
import { 
  BusinessIntelligence, 
  BusinessIntelligenceCreate, 
  BusinessIntelligenceUpdate,
  CompetitorCreate,
  AIAnalysisRequest,
  AIAnalysisResponse,
  BusinessIntelligenceValidation
} from '../types/business-intelligence';

// Updated to use project-centric URL structure
const BI_BASE_URL = '/api/v1/projects';

/**
 * Get business intelligence profile for a project
 */
export const getBusinessIntelligence = async (projectId: string): Promise<BusinessIntelligence> => {
  return get<BusinessIntelligence>(`${BI_BASE_URL}/${projectId}/business-intelligence`);
};

/**
 * Create business intelligence profile for a project
 */
export const createBusinessIntelligence = async (
  projectId: string, 
  data: BusinessIntelligenceCreate
): Promise<BusinessIntelligence> => {
  return post<BusinessIntelligence>(`${BI_BASE_URL}/${projectId}/business-intelligence`, data);
};

/**
 * Update business intelligence profile for a project
 */
export const updateBusinessIntelligence = async (
  projectId: string, 
  data: BusinessIntelligenceUpdate
): Promise<BusinessIntelligence> => {
  return put<BusinessIntelligence>(`${BI_BASE_URL}/${projectId}/business-intelligence`, data);
};

/**
 * Add a competitor to the business intelligence profile
 */
export const addCompetitor = async (
  projectId: string, 
  data: CompetitorCreate
): Promise<BusinessIntelligence> => {
  return post<BusinessIntelligence>(`${BI_BASE_URL}/${projectId}/business-intelligence/competitors`, data);
};

/**
 * Request AI analysis of business intelligence data
 */
export const requestAIAnalysis = async (
  projectId: string, 
  analysisRequest: AIAnalysisRequest
): Promise<AIAnalysisResponse> => {
  return post<AIAnalysisResponse>(`${BI_BASE_URL}/${projectId}/business-intelligence/ai-analysis`, analysisRequest);
};

/**
 * Validate business intelligence data completeness
 */
export const validateBusinessIntelligence = async (
  projectId: string
): Promise<BusinessIntelligenceValidation> => {
  return get<BusinessIntelligenceValidation>(`${BI_BASE_URL}/${projectId}/business-intelligence/validation`);
};

/**
 * Google Places API Integration Functions
 */

/**
 * Discover potential competitors using Google Places API
 */
export const discoverCompetitors = async (
  projectId: string,
  location?: string,
  radiusMiles?: number,
  keyword?: string
): Promise<{
  search_location: string;
  radius_miles: number;
  keyword?: string;
  total_found: number;
  competitors: Array<{
    place_id: string;
    name: string;
    address: string;
    phone?: string;
    website?: string;
    rating?: number;
    user_ratings_total?: number;
    price_level?: number;
    business_status: string;
    types: string[];
    distance_miles?: number;
    distance_meters?: number;
    vicinity: string;
    photos: Array<{
      photo_reference: string;
      height: number;
      width: number;
    }>;
    opening_hours?: {
      open_now?: boolean;
      weekday_text: string[];
    };
    review_summary: {
      total_reviews: number;
      recent_reviews: Array<{
        author_name: string;
        rating: number;
        text: string;
        relative_time: string;
      }>;
    };
  }>;
}> => {
  const params = new URLSearchParams();
  if (location) params.append('location', location);
  if (radiusMiles) params.append('radius_miles', radiusMiles.toString());
  if (keyword) params.append('keyword', keyword);

  return get(`${BI_BASE_URL}/${projectId}/business-intelligence/discover-competitors?${params.toString()}`);
};

/**
 * Get detailed business information from Google Places
 */
export const getBusinessDetails = async (
  projectId: string,
  placeId: string
): Promise<{
  place_id: string;
  name: string;
  formatted_address: string;
  formatted_phone_number?: string;
  international_phone_number?: string;
  website?: string;
  rating?: number;
  user_ratings_total?: number;
  price_level?: number;
  types: string[];
  business_status: string;
  url: string;
  vicinity: string;
  utc_offset?: number;
  geometry: {
    lat: number;
    lng: number;
  };
  opening_hours?: {
    open_now: boolean;
    periods: any[];
    weekday_text: string[];
  };
  photos: Array<{
    photo_reference: string;
    height: number;
    width: number;
    html_attributions: string[];
  }>;
  reviews: Array<{
    author_name: string;
    author_url: string;
    language: string;
    profile_photo_url: string;
    rating: number;
    relative_time_description: string;
    text: string;
    time: number;
  }>;
}> => {
  return get(`${BI_BASE_URL}/${projectId}/business-intelligence/business-details/${placeId}`);
};

/**
 * Add a competitor from Google Places data
 */
export const addCompetitorFromGoogle = async (
  projectId: string,
  placeId: string,
  competitorName?: string,
  competitiveRelationship: string = 'direct',
  notes?: string
): Promise<BusinessIntelligence> => {
  return post<BusinessIntelligence>(`${BI_BASE_URL}/${projectId}/business-intelligence/add-competitor-from-google`, {
    place_id: placeId,
    competitor_name: competitorName,
    competitive_relationship: competitiveRelationship,
    notes
  });
};

/**
 * Fetch business data from Google Places to populate company profile
 */
export const fetchFromGoogle = async (
  projectId: string,
  businessName: string,
  address?: string
): Promise<{
  place_id: string;
  name: string;
  formatted_address: string;
  formatted_phone_number?: string;
  website?: string;
  rating?: number;
  user_ratings_total?: number;
  price_level?: number;
  types: string[];
  business_status: string;
  vicinity: string;
  opening_hours?: {
    open_now: boolean;
    weekday_text: string[];
  };
  photos: Array<{
    photo_reference: string;
    height: number;
    width: number;
  }>;
  reviews_summary: {
    total_reviews: number;
    recent_reviews: Array<{
      author_name: string;
      rating: number;
      text: string;
    }>;
  };
}> => {
  return post(`${BI_BASE_URL}/${projectId}/business-intelligence/fetch-from-google`, {
    business_name: businessName,
    address
  });
};

/**
 * AI-Enhanced Data Collection Functions
 */

/**
 * AI-assisted business profile enhancement
 */
export const enhanceBusinessProfile = async (
  projectId: string,
  businessName: string,
  website?: string,
  address?: string
): Promise<{
  enhanced_data: {
    attractions: Array<{
      name: string;
      type: string;
      confidence: number;
    }>;
    membership_plans: Array<{
      name: string;
      price: number;
      tier: string;
      confidence: number;
    }>;
    food_and_beverage: {
      menu_categories: string[];
      signature_items: string[];
      confidence: number;
    };
    operational_ratings: {
      google_rating?: number;
      total_reviews?: number;
      cleanliness_estimate?: number;
    };
  };
  data_sources: string[];
  confidence_score: number;
}> => {
  return post(`/api/v1/external/openai/enhance-business-profile`, {
    project_id: projectId,
    business_name: businessName,
    website,
    address
  });
};

/**
 * AI-assisted pricing analysis
 */
export const analyzePricing = async (
  projectId: string,
  competitorData: Array<{
    name: string;
    website?: string;
    location: string;
  }>
): Promise<{
  pricing_analysis: {
    membership_pricing: {
      tier: string;
      price_range: { min: number; max: number };
      average: number;
    }[];
    birthday_packages: {
      tier: string;
      price_per_child_range: { min: number; max: number };
      average: number;
    }[];
    recommendations: string[];
  };
  market_position: string;
  confidence_score: number;
}> => {
  return post(`/api/v1/external/openai/analyze-pricing`, {
    project_id: projectId,
    competitors: competitorData
  });
};

/**
 * AI-assisted market gap analysis
 */
export const analyzeMarketGaps = async (
  projectId: string,
  location: string,
  targetDemographic: string
): Promise<{
  market_gaps: Array<{
    opportunity: string;
    potential_revenue: string;
    implementation_difficulty: string;
    recommendation: string;
  }>;
  market_saturation: {
    level: string;
    explanation: string;
  };
  growth_opportunities: string[];
  recommendations: string[];
}> => {
  return post(`/api/v1/external/openai/analyze-market-gaps`, {
    project_id: projectId,
    location,
    target_demographic: targetDemographic
  });
};

/**
 * Utility functions for form handling
 */

/**
 * Convert form data to API format
 */
export const convertFormToApiData = (formData: any): BusinessIntelligenceCreate => {
  return {
    industry: 'entertainment',
    sub_industry: formData.subIndustry,
    company_profile: {
      location_name: formData.companyProfile.locationName,
      address: formData.companyProfile.address,
      is_existing_location: formData.companyProfile.isExistingLocation,
      opening_date: formData.companyProfile.openingDate?.toISOString(),
      source_info: {
        website: formData.companyProfile.website,
        is_franchise: formData.companyProfile.isFranchise,
        franchise_name: formData.companyProfile.franchiseName,
        has_fdd: false,
        data_confidence: 3
      },
      attractions: formData.attractions || [],
      membership_plans: formData.membershipPlans || [],
      birthday_packages: formData.birthdayPackages || [],
      food_and_beverage: formData.foodAndBeverage,
      cleanliness: formData.operationalRatings?.cleanliness,
      staff_engagement: formData.operationalRatings?.staffEngagement,
      safety_rating: formData.operationalRatings?.safetyRating,
      marketing_channels: formData.marketingChannels || [],
      google_rating: formData.onlinePresence?.googleRating,
      total_reviews: formData.onlinePresence?.totalReviews,
      review_notes: formData.onlinePresence?.reviewNotes
    },
    competitor_profiles: []
  };
};

/**
 * Convert API data to form format
 */
export const convertApiToFormData = (apiData: BusinessIntelligence): any => {
  return {
    subIndustry: apiData.sub_industry,
    companyProfile: {
      locationName: apiData.company_profile.location_name,
      address: apiData.company_profile.address,
      isExistingLocation: apiData.company_profile.is_existing_location,
      openingDate: apiData.company_profile.opening_date ? new Date(apiData.company_profile.opening_date) : undefined,
      website: apiData.company_profile.source_info.website,
      isFranchise: apiData.company_profile.source_info.is_franchise,
      franchiseName: apiData.company_profile.source_info.franchise_name
    },
    attractions: apiData.company_profile.attractions,
    membershipPlans: apiData.company_profile.membership_plans,
    birthdayPackages: apiData.company_profile.birthday_packages,
    foodAndBeverage: apiData.company_profile.food_and_beverage,
    operationalRatings: {
      cleanliness: apiData.company_profile.cleanliness,
      staffEngagement: apiData.company_profile.staff_engagement,
      safetyRating: apiData.company_profile.safety_rating
    },
    marketingChannels: apiData.company_profile.marketing_channels,
    onlinePresence: {
      googleRating: apiData.company_profile.google_rating,
      totalReviews: apiData.company_profile.total_reviews,
      reviewNotes: apiData.company_profile.review_notes
    }
  };
};

/**
 * Error handling wrapper for BI operations
 */
export const withErrorHandling = async <T>(
  operation: () => Promise<T>,
  errorMessage: string
): Promise<T | null> => {
  try {
    return await operation();
  } catch (error) {
    console.error(errorMessage, error);
    if (error instanceof ApiError) {
      throw error;
    }
    throw new Error(errorMessage);
  }
};

// Export a service object for easier import in stores
export const businessIntelligenceService = {
  getBusinessIntelligence,
  createBusinessIntelligence,
  updateBusinessIntelligence,
  addCompetitor,
  requestAIAnalysis,
  validateBusinessIntelligence,
  discoverCompetitors,
  getBusinessDetails,
  addCompetitorFromGoogle,
  fetchFromGoogle,
  enhanceBusinessProfile,
  analyzePricing,
  analyzeMarketGaps,
  convertFormToApiData,
  convertApiToFormData,
  withErrorHandling
};
